import { useCallback, useEffect, useState } from 'react';
import { fetchMineImpressions, ImpressionApiError, updateImpression, type GuestImpression, type ImpressionPayload } from '../guestImpressionsApi';
import {
  fetchPublicationRestriction,
  publicationDecisionUrl,
  publicationRestrictionMessage,
  type PublicationRestriction,
} from '../publicationRestriction';
import { ImpressionEditor } from './ImpressionEditor';
import '../impressions.css';

type ImpressionEditScreenProps = { impressionId: string; onDone: (message: string) => void; onCancel: () => void };

export function ImpressionEditScreen({ impressionId, onDone, onCancel }: ImpressionEditScreenProps) {
  const [impression, setImpression] = useState<GuestImpression | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const [restriction, setRestriction] = useState<PublicationRestriction | null>(null);
  const [restrictionChecking, setRestrictionChecking] = useState(true);

  const refreshRestriction = useCallback(async (): Promise<PublicationRestriction | null> => {
    setRestrictionChecking(true);
    try {
      const next = await fetchPublicationRestriction();
      setRestriction(next);
      const message = publicationRestrictionMessage(next);
      if (message) setFlash(message);
      return next;
    } catch {
      setFlash('Не удалось проверить возможность публикации. Попробуйте ещё раз — изменения останутся на экране.');
      return null;
    } finally {
      setRestrictionChecking(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchMineImpressions()
      .then((response) => {
        if (!alive) return;
        const found = response.data.find((item) => item.id === impressionId) ?? null;
        setImpression(found);
        setFlash(found ? null : 'Впечатление не найдено');
        setLoading(false);
      })
      .catch(() => { if (alive) { setFlash('Не удалось загрузить впечатление'); setLoading(false); } });
    return () => { alive = false; };
  }, [impressionId]);

  useEffect(() => {
    void refreshRestriction();
    const onFocus = () => { void refreshRestriction(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshRestriction]);

  const submit = async (payload: ImpressionPayload) => {
    setSubmitting(true);
    setFieldErrors({});
    setFlash(null);
    try {
      const currentRestriction = await fetchPublicationRestriction();
      setRestriction(currentRestriction);
      const blockedMessage = publicationRestrictionMessage(currentRestriction);
      if (blockedMessage) { setFlash(blockedMessage); return; }

      await updateImpression(impressionId, payload);
      onDone('Изменения сохранены');
    } catch (error) {
      if (error instanceof ImpressionApiError) {
        if (error.status === 409 && error.code === 'impression_edit_window_expired') { onDone('Редактирование доступно только в первые сутки'); return; }
        if (error.status === 403) {
          const refreshed = await refreshRestriction();
          setFlash(refreshed ? publicationRestrictionMessage(refreshed) ?? error.message : error.message);
          return;
        }
        if (error.status === 503) { setFlash('Проверка возможности публикации временно недоступна. Попробуйте позже — изменения останутся на экране.'); return; }
        if (error.status === 422) { setFieldErrors(error.fields); setFlash('Проверьте заполненные поля'); return; }
        if (error.status === 429) { setFlash('Слишком много попыток, подождите минуту. Изменения сохранены на экране.'); return; }
      }
      setFlash('Не удалось сохранить изменения. Введённые данные остаются на экране.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <main className="impression-screen impression-screen--state"><div className="impression-state-card">Загружаем впечатление…</div></main>;
  if (!impression) return <main className="impression-screen impression-screen--state"><div className="impression-state-card"><strong>{flash ?? 'Впечатление не найдено'}</strong><button className="impression-primary-button" type="button" onClick={onCancel}>Вернуться в профиль</button></div></main>;

  const restrictionMessage = restriction ? publicationRestrictionMessage(restriction) : null;
  const restrictionDecisionUrl = restriction ? publicationDecisionUrl(restriction) : null;
  return (
    <ImpressionEditor
      key={impression.id}
      title={`Ваше впечатление о ${impression.venue.display_name ?? 'месте Hopper'}`}
      initial={{ verdict: impression.verdict, text: impression.text, tags: impression.tags, is_public: impression.is_public }}
      submitLabel={restrictionMessage ? 'Публикация ограничена' : 'Сохранить изменения'}
      submitting={submitting}
      submitDisabled={restrictionChecking || Boolean(restrictionMessage)}
      fieldErrors={fieldErrors}
      screenFlash={flash}
      mediaSlot={restrictionMessage && restrictionDecisionUrl ? (
        <button className="impression-secondary-button" type="button" onClick={() => window.location.assign(restrictionDecisionUrl)}>
          Посмотреть решение
        </button>
      ) : null}
      onSubmit={(payload) => void submit(payload)}
      onCancel={onCancel}
    />
  );
}
